import { useState, useEffect, useCallback, useMemo } from "react";
import { Badge, DataTable, Link, Card, Filters, Checkbox, Box, InlineStack, BlockStack, Text } from "@shopify/polaris";
import QuickViewSalesOrderComponent from "./quickViewComponent";
import { addFilters, applyFilters, filtersToApiUrl, handleFilterChange, limitOptions, removeAllFilters, useUrl } from "../../../shared/filters/filterfunctions";
import { useLocation, useNavigate } from "react-router-dom";
import { getRequest, getTableFooter, handleSaveOnEnter, truncate } from "../../../shared/components/functions";
import { PageSpinner } from "../../../shared/components/spinner/PageSpinner";
import UpdateDeliveryTermModal from "./updateDeliveryTermModal";
import { CardHeading } from "../../../shared/components/CardHeading";
import ConfirmDeliveryTerm from "../confirmDeliveryTerm";

const ORDER_NUMBER_COLUMN_INDEX = 0;
const DELIVERY_TERM_COLUMN_INDEX = 8;
const MACHINE_PRODUCT_INFO = {
  10957: { label: "Jack", ratePerDay: 5, accent: "#8e51ff" },
  10956: { label: "Greg", ratePerDay: 10, accent: "#0f9d58" },
};
const MACHINE_PRODUCT_IDS = [10957, 10956];

const formatWorkDays = (value) => {
  if (!value) return "0";
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
};

const formatEstimatedDate = (date) => {
  if (!date) return "No open machines";
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const getEstimatedFinishDate = (workDays) => {
  if (workDays <= 0) return null;

  const finishDate = new Date();
  finishDate.setHours(12, 0, 0, 0);

  while (finishDate.getDay() === 0 || finishDate.getDay() === 6) {
    finishDate.setDate(finishDate.getDate() + 1);
  }

  let remainingBusinessDays = Math.ceil(workDays) - 1;

  while (remainingBusinessDays > 0) {
    finishDate.setDate(finishDate.getDate() + 1);

    if (finishDate.getDay() !== 0 && finishDate.getDay() !== 6) {
      remainingBusinessDays -= 1;
    }
  }

  return finishDate;
};

const StatBox = ({ title, value, accent }) => (
  <div
    style={{
      border: "1px solid #dfe3e8",
      borderRadius: "8px",
      padding: "12px 14px",
      minHeight: "78px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      gap: "4px",
      background: "#f6f6f7",
      borderTop: `2px solid ${accent || "#5c6ac4"}`,
    }}>
    <BlockStack gap="100">
      <Text as="p" tone="subdued">
        {title}
      </Text>
      <Text variant="headingMd" as="p">
        {value}
      </Text>
    </BlockStack>
  </div>
);

export default function FulfilmentOrders() {
  const url = useUrl();
  const location = useLocation();
  const navigate = useNavigate();

  const [loaded, setLoaded] = useState(false);
  const [machineOverviewLoaded, setMachineOverviewLoaded] = useState(false);
  const [items, setItems] = useState([]);
  const [openMachineLines, setOpenMachineLines] = useState([]);
  const [showFulfilledOrders, setShowFulfilledOrders] = useState(false);
  const [sortColumnIndex, setSortColumnIndex] = useState(DELIVERY_TERM_COLUMN_INDEX);
  const [sortDirection, setSortDirection] = useState("ascending");
  const [filters, setFilters] = useState(
    new Map([
      ["limit", { type: "limit", name: "Limit", value: url.get("limit") || "100", options: limitOptions }],
      ["page", { type: "page", name: "Page", value: url.get("page") || "1" }],
      ["q", { type: "text", name: "Q", value: url.get("q") || "" }],
      ["customer", { type: "text", name: "Customer", value: url.get("customer") || "" }],
      ["reference", { type: "text", name: "Reference", value: url.get("reference") || "" }],
    ])
  );

  useEffect(() => {
    fetchItems();
  }, [filters, showFulfilledOrders, sortColumnIndex, sortDirection]);

  useEffect(() => {
    setSortColumnIndex(showFulfilledOrders ? ORDER_NUMBER_COLUMN_INDEX : DELIVERY_TERM_COLUMN_INDEX);
    setSortDirection(showFulfilledOrders ? "descending" : "ascending");
  }, [showFulfilledOrders]);

  useEffect(() => {
    fetchOpenMachineLines();
  }, []);

  const fetchItems = useCallback(async () => {
    let filterString = filtersToApiUrl(filters);
    let sortString = "";
    if (sortColumnIndex === DELIVERY_TERM_COLUMN_INDEX) {
      sortString = `&sort_by=delivery_term&sort_direction=${sortDirection === "descending" ? "desc" : "asc"}`;
    } else if (sortColumnIndex === ORDER_NUMBER_COLUMN_INDEX) {
      sortString = `&sort_by=order_number&sort_direction=${sortDirection === "ascending" ? "asc" : "desc"}`;
    }
    const data = await getRequest(`/api/fulfilment?${filterString}&showFulfilledOrders=${showFulfilledOrders}${sortString}`);
    setItems(data);
    if (window.location.pathname === location.pathname) navigate(`${window.location.pathname}?${filterString}`);
    setLoaded(true);
  }, [filters, location.pathname, navigate, showFulfilledOrders, sortColumnIndex, sortDirection]);

  const fetchOpenMachineLines = useCallback(async () => {
    const data = await getRequest("/api/fulfilment/open-machine-lines");
    setOpenMachineLines(Array.isArray(data) ? data : []);
    setMachineOverviewLoaded(true);
  }, []);

  const fulfilment_status = (toFulfill, totalItems) => {
    if (toFulfill === 0) return <Badge progress="complete">Fulfilled</Badge>;
    else if (toFulfill === totalItems) return <Badge progress="incomplete" status="attention">{`Not fulfilled (0/${totalItems})`}</Badge>;
    return <Badge tone="warning" progress="partiallyComplete">{`Partially fulfilled (${totalItems - toFulfill}/${totalItems})`}</Badge>;
  };

  const formatDeliveryTerm = (deliveryTerm) => {
    if (!deliveryTerm) return "No date";
    if (deliveryTerm === "TBD-WEBSHOP") return deliveryTerm;
    return String(deliveryTerm).substring(0, 10);
  };

  const getDaysLeftBadge = (item) => {
    if (item.items_remaining_to_fulfill === 0) return <Badge tone="success">Fulfilled</Badge>;
    if (item.Leveringstermijn === "TBD-WEBSHOP") return <Badge tone="critical">Set date</Badge>;
    if (item.due_diff === null || item.due_diff === undefined || item.due_diff === "") return <Badge>Unknown</Badge>;

    const dueDiff = Number(item.due_diff);
    if (Number.isNaN(dueDiff)) return <Badge>Unknown</Badge>;
    if (dueDiff < 0) return <Badge tone="critical">{`${Math.abs(dueDiff)} day${Math.abs(dueDiff) === 1 ? "" : "s"} overdue`}</Badge>;
    if (dueDiff === 0) return <Badge tone="critical">Today</Badge>;
    if (dueDiff > 14) return <Badge tone="success">{`${dueDiff} day${dueDiff === 1 ? "" : "s"}`}</Badge>;
    if (dueDiff >= 7) return <Badge tone="attention">{`${dueDiff} day${dueDiff === 1 ? "" : "s"}`}</Badge>;
    return <Badge tone="critical">{`${dueDiff} day${dueDiff === 1 ? "" : "s"}`}</Badge>;
  };

  const handleSort = (columnIndex, direction) => {
    if (columnIndex !== ORDER_NUMBER_COLUMN_INDEX && columnIndex !== DELIVERY_TERM_COLUMN_INDEX) return;
    setSortColumnIndex(columnIndex);
    setSortDirection(direction);
  };

  const machineOverview = useMemo(() => {
    const counts = MACHINE_PRODUCT_IDS.reduce((acc, productId) => {
      acc[productId] = 0;
      return acc;
    }, {});

    openMachineLines.forEach((line) => {
      const productId = Number(line.produktnr);
      if (Object.prototype.hasOwnProperty.call(counts, productId)) {
        counts[productId] += 1;
      }
    });

    const gregCount = counts[10956] || 0;
    const jackCount = counts[10957] || 0;
    const gregWorkDays = gregCount / MACHINE_PRODUCT_INFO[10956].ratePerDay;
    const jackWorkDays = jackCount / MACHINE_PRODUCT_INFO[10957].ratePerDay;
    const totalWorkDays = gregWorkDays + jackWorkDays;

    return {
      totalMachines: openMachineLines.length,
      gregCount,
      jackCount,
      gregWorkDays,
      jackWorkDays,
      totalWorkDays,
      estimatedFinishDate: getEstimatedFinishDate(totalWorkDays),
    };
  }, [openMachineLines]);

  const rows = Array.isArray(items)
    ? items.map((item) => {
        const serialsToConnect = item.serials_connected_needed - item.serials_connected_qty;

        return [
          <Link removeUnderline url={`/sales/${item.OrderbevestigingID}`}>
            {item.Orderbevestigingnummer}
          </Link>,
          <QuickViewSalesOrderComponent id={item.OrderbevestigingID} />,
          <Badge>{item.Afkorting}</Badge>,
          String(item.Datum).substring(0, 10),
          truncate(item.RefKlant, 20),
          <InlineStack gap="100" blockAlign="center">
            <img
              src={`https://flagcdn.com/w20/${String(item.ISO2).toLowerCase()}.png`}
              srcSet={`https://flagcdn.com/w40/${String(item.ISO2).toLowerCase()}.png 2x,https://flagcdn.com/w60/${String(item.ISO2).toLowerCase()}.png 3x`}
              width="18"
              height="13"
              alt={`${String(item.ISO2).toLowerCase()}`}></img>
            {truncate(item.NAAM, 30)}
          </InlineStack>,
          fulfilment_status(item.items_remaining_to_fulfill, item.total_items),
          <Badge tone={serialsToConnect > 0 ? "critical" : ""} progress={serialsToConnect === 0 ? "complete" : item?.serials_connected_qty > 0 ? "partiallyComplete" : "incomplete"}>
            {`Serials (${item?.serials_connected_qty}/${item?.serials_connected_needed})`}
          </Badge>,
          <div style={{ display: "flex", alignItems: "center", gap: "4px", flexWrap: "wrap" }}>
            {item.Leveringstermijn === "TBD-WEBSHOP" ? (
              <>
                <Badge tone="critical">{formatDeliveryTerm(item.Leveringstermijn)}</Badge>
                <ConfirmDeliveryTerm order_id={item.OrderbevestigingID} callback={fetchItems} />
              </>
            ) : (
              <UpdateDeliveryTermModal order_id={item.OrderbevestigingID} deliveryTerm={formatDeliveryTerm(item.Leveringstermijn)} callback={fetchItems} />
            )}
          </div>,
          getDaysLeftBadge(item),
          // <FulfillerSelectComponent
          //   fetchItems={fetchItems}
          //   user_id={item?.Afkorting}
          //   options={users}
          //   order_id={item.OrderbevestigingID}
          // />,
        ];
      })
    : [];

  if (!loaded || !machineOverviewLoaded) return <PageSpinner />;

  return (
    <BlockStack gap="400">
      <Card>
        <Box padding="300">
          <BlockStack gap="200">
            <Text variant="headingSm" as="h2">
              Open machines
            </Text>
            <div style={{ overflowX: "auto" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(130px, 1fr))", gap: "12px", minWidth: "860px" }}>
                <StatBox title="Open total" value={machineOverview.totalMachines} accent="#1f6feb" />
                <StatBox title="Jack (10957)" value={machineOverview.jackCount} accent={MACHINE_PRODUCT_INFO[10957].accent} />
                <StatBox title="Greg (10956)" value={machineOverview.gregCount} accent={MACHINE_PRODUCT_INFO[10956].accent} />
                <StatBox title="Estimated production time Jack" value={`${formatWorkDays(machineOverview.jackWorkDays)} d`} accent={MACHINE_PRODUCT_INFO[10957].accent} />
                <StatBox title="Estimated production time Greg" value={`${formatWorkDays(machineOverview.gregWorkDays)} d`} accent={MACHINE_PRODUCT_INFO[10956].accent} />
                <StatBox title="Estimated production finish time" value={formatEstimatedDate(machineOverview.estimatedFinishDate)} accent="#2c6e49" />
              </div>
            </div>
          </BlockStack>
        </Box>
      </Card>

      <Card>
        <CardHeading title="Orders to fulfil" />
        <div onKeyDown={(e) => handleSaveOnEnter(e, fetchItems)}>
          <Filters
            queryValue={filters.get("q").value}
            filters={addFilters(filters, setFilters)}
            appliedFilters={applyFilters(filters, setFilters)}
            onClearAll={() => removeAllFilters(filters, setFilters)}
            onQueryChange={(e) => handleFilterChange(e, "q", filters.get("q").value, setFilters)}
            onQueryClear={() => handleFilterChange("", "q", filters.get("q").value, setFilters)}
            onQueryBlur={fetchItems}
            autoComplete={false}
            queryPlaceholder="Filter orders"
          />
        </div>
        <Box paddingInlineStart="200">
          <Checkbox checked={showFulfilledOrders} label="Show fulfilled orders" onChange={() => setShowFulfilledOrders(!showFulfilledOrders)} />
        </Box>
        <DataTable
          key={showFulfilledOrders ? "fulfilment-all-orders" : "fulfilment-open-orders"}
          columnContentTypes={["text", "text", "text", "text", "text", "text", "text", "text", "text", "text"]}
          headings={[<b>Number</b>, <b>View</b>, <b>User</b>, <b>Date</b>, <b>Reference</b>, <b>Customer</b>, <b>Fulfilment</b>, <b>Serials</b>, <b>Delivery term</b>, <b>Days left</b>]}
          verticalAlign="middle"
          increasedTableDensity
          sortable={[true, false, false, false, false, false, false, false, true, false]}
          defaultSortDirection={showFulfilledOrders ? "descending" : "ascending"}
          initialSortColumnIndex={showFulfilledOrders ? ORDER_NUMBER_COLUMN_INDEX : DELIVERY_TERM_COLUMN_INDEX}
          onSort={handleSort}
          rows={rows}
          footerContent={getTableFooter(rows, filters.get("page"), filters.get("limit"), setFilters, "orders")}
        />
      </Card>
    </BlockStack>
  );
}
